import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';

import { PromotionModel } from '@bytel/bytel-sales';
import { FundingMethod, LimitedCommercialSeries } from '@bytel/checkout';

import {
    ERROR_MODAL_MOCK,
    INFO_MODAL_MOCK,
    OTHER_MODAL_MOCK,
    SUCCESS_MODAL_MOCK,
} from '@base/mocks-handler/mocks/modal.mock';

import { MessageService } from '@services/message.service';

import { ByouErrorModalComponent } from '@shared/byou-error-modal/byou-error-modal.component';
import { CdcPaymentModalComponent } from '@shared/cdc-payment-modal/cdc-payment-modal.component';
import { CustomerErrorModalComponent } from '@shared/customer-error-modal/customer-error-modal.component';
import { DestinationModalComponent } from '@shared/destination-modal/destination-modal.component';
import { ErrorModalRetryComponent } from '@shared/error-modal-retry/error-modal-retry.component';
import { ErrorModalComponent } from '@shared/error-modal/error-modal.component';
import { LoadingModalComponent } from '@shared/loading-modal/loading-modal.component';
import { PartnerExclusivityErrorModalComponent } from '@shared/partner-exclusivity-error-modal/partner-exclusivity-error-modal.component';
import { PaymentErrorModalComponent } from '@shared/payment-error-modal/payment-error-modal.component';
import { ProLegalRepresentativeErrorModalComponent } from '@shared/pro-legal-representative-error-modal/pro-legal-representative-error-modal.component';
import { ModalPricesCompatibilityComponent } from '@shared/promotion/coupon/modal/modal-prices-compatibility/modal-prices-compatibility.component';

import { EligiblityErrorModalComponent } from '../../shared/eligiblity-error-modal/eligiblity-error-modal.component';
import { FtthEligibilityErrorModalComponent } from '../../shared/ftth-eligibility-error-modal/ftth-eligibility-error-modal.component';
import { SwappyModalComponent } from '../../shared/swappy-modal/swappy-modal.component';
import { ModalService } from '../../trilogy/modal/modal.service';
import { ClickCollectIncompatibilityModalComponent } from '../../tunnel/crosssell/click-collect-modal/click-collect-modal.component';
import { ScoringModalComponent } from '../../tunnel/payment/components/scoring-modal/scoring-modal.component';

const MODAL_TYPE = {
    ERROR_DEFAULT: 'Default',
    ERROR_BYOU: 'B&You',
    ERROR_CUSTOMER: 'Customer',
    ERROR_RETRY: 'Retry',
    ERROR_PARTNER_EXCLUSIVITY: 'PartnerExclusivity',
    ERROR_PRO_EXCLUSIVITY: 'ProExclusivity',
    ERROR_PRO_LEGAL_REPRESENTATIVE: 'ProLegalRepresent',
    ERROR_PAYMENT: 'Payment',
    INFO_PRO_PRICE: 'ProPrice',
    OTHER_PRICE_COMPATIBILITY: 'PriceCompatibility',
    OTHER_LOADING: 'Loading',
    OTHER_DESTINATION: 'Destination',
    INFO_CDC_PAYMENT: 'CdcPayment',
    ERROR_ELIGIBILITY_PANENKA: 'EligiblityErrorModalPanenka',
    ERROR_ELIGIBILITY_BBOXBYOU: 'EligiblityErrorModalByou',
    SCORING: 'Scoring',
    FTTH: 'Ftth',
    CLICK_COLLECT_INCOMPATIBILITY: 'ClickCollectIncompatibility',
    SWAPPY: 'Swappy',
};

export interface ModalsConfig {
    label: string;
    type: string;
}

/** @dynamic */
@Component({
    selector: 'checkout-mockups-modal',
    templateUrl: './mockups-modal.component.html',
    styleUrls: ['./mockups-modal.scss'],
    standalone: true,
    imports: [CommonModule],
})
export class MockupsModalComponent {
    public errorsModal = ERROR_MODAL_MOCK;
    public successModal = SUCCESS_MODAL_MOCK;
    public infosModal = INFO_MODAL_MOCK;
    public othersModal = OTHER_MODAL_MOCK;

    private modalService = inject(ModalService);
    private messageService = inject(MessageService);

    public openModal(modal: ModalsConfig): void {
        switch (modal.type) {
            case MODAL_TYPE.ERROR_DEFAULT:
                this.messageService.openModal(ErrorModalComponent);
                break;
            case MODAL_TYPE.ERROR_BYOU:
                this.messageService.openModal(ByouErrorModalComponent);
                break;
            case MODAL_TYPE.ERROR_CUSTOMER:
                this.messageService.openModal(CustomerErrorModalComponent);
                break;
            case MODAL_TYPE.ERROR_RETRY:
                this.messageService.openModal(ErrorModalRetryComponent);
                break;
            case MODAL_TYPE.ERROR_PARTNER_EXCLUSIVITY:
                this.messageService.openModal(PartnerExclusivityErrorModalComponent, { partner: 'Mock partner' });
                break;
            case MODAL_TYPE.ERROR_PAYMENT:
                const modal = this.modalService.open<PaymentErrorModalComponent>(PaymentErrorModalComponent);
                modal.componentInstance.younitedRedirectionError = true;
                break;
            case MODAL_TYPE.ERROR_PRO_LEGAL_REPRESENTATIVE:
                this.messageService.openModal(ProLegalRepresentativeErrorModalComponent);
                break;

            case MODAL_TYPE.OTHER_PRICE_COMPATIBILITY:
                this.messageService.openModal(
                    ModalPricesCompatibilityComponent,
                    {
                        products: {
                            toAdd: new PromotionModel({}),
                            toDelete: new FundingMethod({}),
                        },
                    },
                    'medium',
                );
                break;
            case MODAL_TYPE.OTHER_LOADING:
                this.messageService.openModal(LoadingModalComponent);
                break;
            case MODAL_TYPE.OTHER_DESTINATION:
                this.messageService.openModal(DestinationModalComponent);
                break;
            case MODAL_TYPE.INFO_CDC_PAYMENT:
                this.messageService.openModal(CdcPaymentModalComponent);
                break;
            case MODAL_TYPE.ERROR_ELIGIBILITY_BBOXBYOU:
                this.messageService.openModal(EligiblityErrorModalComponent, {
                    commercialSeries: LimitedCommercialSeries.B_AND_YOU,
                });
                break;
            case MODAL_TYPE.SCORING:
                this.messageService.openModal(ScoringModalComponent, {});
                break;
            case MODAL_TYPE.FTTH:
                this.messageService.openModal(FtthEligibilityErrorModalComponent, {});
                break;
            case MODAL_TYPE.CLICK_COLLECT_INCOMPATIBILITY:
                this.messageService.openModal(ClickCollectIncompatibilityModalComponent, {});
                break;
            case MODAL_TYPE.SWAPPY:
                this.messageService.openModal(SwappyModalComponent, {
                    hasSwappyError: true,
                    title: 'Le paiement avec Smart Change ne peut être finalisé en ligne',
                });
                break;
        }
    }
}
